import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ConfirmationNumber as CouponIcon,
  Add as AddIcon,
  LocalOffer as LocalOfferIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { multiTenantCouponAPI } from '../../services/api';
import ModernButton from '../../components/Common/ModernButton';
import ModernCard from '../../components/Common/ModernCard';
import ModernTextField from '../../components/Common/ModernTextField';
import IconContainer from '../../components/Common/IconContainer';
import ResponsivePageHeader from '../../components/Common/ResponsivePageHeader';
import { logger } from '../../utils/logger';
// Types
interface Campaign {
  id: string;
  name: string;
  description: string;
  status?: string | null;
  start_date?: string;
  end_date?: string;
  passkit_campaign_id: string | null;
}

interface Offer {
  id: string;
  campaign_id: string;
  name: string;
  description?: string;
  status?: string;
  campaign_name?: string;
  company_name?: string;
  company_id?: string;
  // PassKit fields
  offerTitle?: string;
  offerShortTitle?: string;
  offerDetails?: string;
  offerFinePrint?: string;
  shortCode?: string;  // camelCase variant
  short_code?: string;  // Backend returns snake_case
  issueStartDate?: string;
  issueEndDate?: string;
  issue_start_date?: string;  // Backend returns snake_case
  issue_end_date?: string;    // Backend returns snake_case
  disabled?: boolean;
  created?: string;
  updated?: string;
  ianaTimezone?: string;
  beforeRedeemPassTemplateId?: string;
  afterRedeemPassTemplateId?: string;
  quota?: any;
  couponExpirySettings?: any;
  redemptionSettings?: any;
}

const CouponOffers: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpiredOffers, setShowExpiredOffers] = useState(true);

  // Helper: Check if offer is expired
  const isOfferExpired = (offer: Offer): boolean => {
    const endDateStr = offer.issue_end_date || offer.issueEndDate;
    if (!endDateStr) return false;
    try {
      const endDate = new Date(endDateStr);
      const now = new Date();
      return now > endDate;
    } catch (e) {
      return false;
    }
  };

  // Statistics
  const totalOffers = offers.length;
  const activeOffers = offers.filter(o => o.status === 'active').length;
  const inactiveOffers = offers.filter(o => o.status !== 'active').length;
  const offersWithCode = offers.filter(o => o.shortCode).length;

  const loadOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const data = await multiTenantCouponAPI.getOffers();
      setOffers(data);
    } catch (error: any) {
      showNotification({ message: 'Greška prilikom učitavanja ponuda: ' + error.message, type: 'error' });
    } finally {
      setOffersLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const handleEdit = (offer: Offer) => {
    setEditingOffer(offer);
    setOpenDialog(true);
  };

  const handleDelete = async (offerId: string, campaignId: string) => {
    if (!globalThis.confirm('Da li ste sigurni da želite da obrišete ovu ponudu?')) return;
    try {
      await multiTenantCouponAPI.deleteOffer(offerId, campaignId);
      showNotification({ message: 'Ponuda uspešno obrisana', type: 'success' });
      loadOffers();
    } catch (error: any) {
      showNotification({ message: 'Greška prilikom brisanja: ' + error.message, type: 'error' });
    }
  };

  const handleSaveOffer = async (offer: Offer) => {
    try {
      if (editingOffer?.id) {
        await multiTenantCouponAPI.updateOffer(editingOffer.id, offer.campaign_id, offer);
        showNotification({ message: 'Ponuda uspešno ažurirana', type: 'success' });
      } else {
        await multiTenantCouponAPI.createOffer(offer.campaign_id, offer);
        showNotification({ message: 'Ponuda uspešno kreirana', type: 'success' });
      }
      setOpenDialog(false);
      setEditingOffer(null);
      loadOffers();
    } catch (error: any) {
      showNotification({ message: 'Greška: ' + error.message, type: 'error' });
    }
  };

  const navigateToCoupons = (offer: Offer) => {
    navigate(`/coupons/coupons?campaign_id=${offer.campaign_id}&offer_id=${offer.id}`);
  };

  const filteredOffers = offers.filter(offer => {
    // Search filter
    const matchesSearch = offer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.shortCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Expired filter
    const matchesExpiredFilter = showExpiredOffers || !isOfferExpired(offer);

    return matchesSearch && matchesExpiredFilter;
  });

  return (
    <Box>
      <ResponsivePageHeader
        title="Upravljanje Ponudama"
        subtitle="Kreirajte i upravljajte ponudama za kupon kampanje"
        actions={
          <ModernButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingOffer(null);
              setOpenDialog(true);
            }}
          >
            Nova Ponuda
          </ModernButton>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <IconContainer color="primary" variant="soft">
                  <LocalOfferIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {totalOffers}
                  </Typography>
                  <Typography color="text.secondary">
                    Ukupno Ponuda
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <IconContainer color="success" variant="soft">
                  <CheckCircleIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {activeOffers}
                  </Typography>
                  <Typography color="text.secondary">
                    Aktivne Ponude
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <IconContainer color="info" variant="soft">
                  <LocalOfferIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {inactiveOffers}
                  </Typography>
                  <Typography color="text.secondary">
                    Neaktivne Ponude
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <IconContainer color="warning" variant="soft">
                  <LocalOfferIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {offersWithCode}
                  </Typography>
                  <Typography color="text.secondary">
                    Ponude sa Kodom
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Search and Table */}
      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              placeholder="Pretraži ponude po imenu, opisu ili kodu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: '300px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                Prikaži istekle:
              </Typography>
              <input
                type="checkbox"
                checked={showExpiredOffers}
                onChange={(e) => setShowExpiredOffers(e.target.checked)}
                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
              />
            </Box>
          </Box>

          {offersLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Naziv</TableCell>
                    {isAdmin && <TableCell>Kompanija</TableCell>}
                    <TableCell>Opis</TableCell>
                    <TableCell>Kod</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Datum Početka</TableCell>
                    <TableCell>Datum Kraja</TableCell>
                    <TableCell>Akcije</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOffers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                        <Typography color="text.secondary">
                          Nema pronađenih ponuda
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOffers.map((offer) => {
                      const isExpired = isOfferExpired(offer);
                      return (
                      <TableRow key={offer.id} sx={isExpired ? { bgcolor: 'rgba(255, 152, 0, 0.08)' } : {}}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {offer.name}
                            {isExpired && (
                              <Chip
                                icon={<WarningIcon />}
                                label="Istekla"
                                size="small"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {offer.company_name || '-'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>{offer.description}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {offer.short_code || offer.shortCode || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={offer.status === 'active' ? 'Aktivna' : 'Neaktivna'}
                            size="small"
                            color={offer.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(offer.issue_start_date || offer.issueStartDate) ? new Date(offer.issue_start_date || offer.issueStartDate!).toLocaleDateString('sr-RS') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={isExpired ? 'error.main' : 'inherit'}>
                            {(offer.issue_end_date || offer.issueEndDate) ? new Date(offer.issue_end_date || offer.issueEndDate!).toLocaleDateString('sr-RS') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => navigateToCoupons(offer)}
                            title="Prikaži kupone"
                          >
                            <CouponIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(offer)}
                            disabled={isExpired}
                            title={isExpired ? 'Ne možete urediti isteklu ponudu' : 'Uredi ponudu'}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(offer.id, offer.campaign_id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </ModernCard>

      {/* Offer Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingOffer ? 'Izmeni Ponudu' : 'Nova Ponuda'}
        </DialogTitle>
        <DialogContent>
          <OfferForm
            offer={editingOffer}
            onSave={handleSaveOffer}
            onCancel={() => {
              setOpenDialog(false);
              setEditingOffer(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Offer Form Component
const OfferForm: React.FC<{
  offer: Offer | null;
  onSave: (offer: Offer) => void;
  onCancel: () => void;
}> = ({ offer, onSave, onCancel }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [formData, setFormData] = useState<Partial<Offer>>(
    offer || {
      campaign_id: '',
      name: '',
      description: '',
      status: 'active',
      issueStartDate: new Date().toISOString(),
      issueEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      beforeRedeemPassTemplateId: '', // REQUIRED by PassKit
      afterRedeemPassTemplateId: '', // Optional - post-redemption template
    }
  );

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const data = await multiTenantCouponAPI.getCampaigns();
        setCampaigns(data.data);
      } catch (error) {
        logger.error('Error loading campaigns:', error);
      }
    };
    loadCampaigns();
  }, []);

  // Load templates when component mounts
  useEffect(() => {
    const loadTemplates = async () => {
      // Wait for campaign to be selected
      if (!formData.campaign_id) {
        setTemplates([]);
        return;
      }

      setLoadingTemplates(true);
      try {
        // Load templates for selected campaign
        const data = await multiTenantCouponAPI.getTemplates(formData.campaign_id);
        setTemplates(data);

        // Auto-select first template if only one available
        if (data.length === 1 && !formData.beforeRedeemPassTemplateId) {
          setFormData(prev => ({
            ...prev,
            beforeRedeemPassTemplateId: data[0].template_id
          }));
        }
      } catch (error) {
        logger.error('Error loading templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, [formData.campaign_id, formData.beforeRedeemPassTemplateId]); // Re-load when campaign changes

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation: Template is required for new offers
    if (!offer && !formData.beforeRedeemPassTemplateId) {
      alert('Molimo izaberite template za ponudu!');
      return;
    }

    onSave(formData as Offer);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Kampanja</InputLabel>
          <Select
            value={formData.campaign_id}
            label="Kampanja"
            onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
            required
            disabled={!!offer}
          >
            {campaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth required={!offer}>
          <InputLabel>Pass Template (Pre Iskorišćenja) *</InputLabel>
          <Select
            value={formData.beforeRedeemPassTemplateId || ''}
            label="Pass Template (Pre Iskorišćenja) *"
            onChange={(e) => setFormData({ ...formData, beforeRedeemPassTemplateId: e.target.value })}
            disabled={!!offer || loadingTemplates}
          >
            {loadingTemplates ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Učitavanje template-ova...
              </MenuItem>
            ) : templates.length === 0 ? (
              <MenuItem disabled>
                Nema dostupnih template-ova
              </MenuItem>
            ) : (
              templates.map((template) => (
                <MenuItem key={template.template_id} value={template.template_id}>
                  {template.name || template.template_id}
                  {template.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      - {template.description}
                    </Typography>
                  )}
                </MenuItem>
              ))
            )}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
            Template određuje izgled kupona PRE iskorišćenja
          </Typography>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Pass Template (Posle Iskorišćenja)</InputLabel>
          <Select
            value={formData.afterRedeemPassTemplateId || ''}
            label="Pass Template (Posle Iskorišćenja)"
            onChange={(e) => setFormData({ ...formData, afterRedeemPassTemplateId: e.target.value })}
            disabled={!!offer || loadingTemplates}
          >
            <MenuItem value="">
              <em>Nije potreban (koristi isti template)</em>
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.template_id} value={template.template_id}>
                {template.name || template.template_id}
                {template.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    - {template.description}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
            Opciono: Izgled kupona POSLE iskorišćenja (može biti drugačiji, npr. sa pečatom "REDEEMED")
          </Typography>
        </FormControl>

        <ModernTextField
          label="Naziv Ponude"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />

        <ModernTextField
          label="Opis"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={3}
        />

        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          >
            <MenuItem value="active">Aktivna</MenuItem>
            <MenuItem value="inactive">Neaktivna</MenuItem>
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <DatePicker
              label="Izdavanje Od"
              value={formData.issueStartDate ? dayjs(formData.issueStartDate) : null}
              onChange={(newValue) => {
                if (newValue && dayjs.isDayjs(newValue)) {
                  setFormData({ ...formData, issueStartDate: newValue.toISOString() });
                } else {
                  setFormData({ ...formData, issueStartDate: '' });
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                }
              }}
            />

            <DatePicker
              label="Izdavanje Do"
              value={formData.issueEndDate ? dayjs(formData.issueEndDate) : null}
              minDate={formData.issueStartDate ? dayjs(formData.issueStartDate) : undefined}
              onChange={(newValue) => {
                if (newValue && dayjs.isDayjs(newValue)) {
                  setFormData({ ...formData, issueEndDate: newValue.toISOString() });
                } else {
                  setFormData({ ...formData, issueEndDate: '' });
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                }
              }}
            />
          </Stack>
        </LocalizationProvider>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onCancel}>
            Otkaži
          </Button>
          <ModernButton type="submit" variant="contained">
            Sačuvaj
          </ModernButton>
        </Box>
      </Box>
    </form>
  );
};

export default CouponOffers;
